import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import api from '../../utils/api';

const PAYMENT_METHODS = [
  { value: 'CASH', label: 'Cash' },
  { value: 'CARD', label: 'Credit/Debit Card' },
  { value: 'CHECK', label: 'Check' },
  { value: 'ACIMA', label: 'Acima Financing' },
  { value: 'SYNCHRONY', label: 'Synchrony Financing' },
  { value: 'SNAP', label: 'Snap Finance' },
  { value: 'PROGRESSIVE', label: 'Progressive Leasing' },
  { value: 'OTHER', label: 'Other' }
];

const PaymentHistory = ({ orderId, onPaymentRecorded, order }) => {
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // New payment form state
  const [newPayment, setNewPayment] = useState({
    amount: '',
    method: 'CASH',
    reference: '',
    notes: ''
  });

  const loadPayments = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get(`/orders/${orderId}/payments`);
      if (res.data?.success) {
        setPaymentData(res.data.data);
      }
    } catch (err) {
      console.error('Failed to load payments:', err);
      setError('Failed to load payment history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orderId) {
      loadPayments();
    }
  }, [orderId]);

  const handleRecordPayment = async () => {
    if (!newPayment.amount || Number(newPayment.amount) <= 0) {
      setError('Please enter a valid payment amount');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      const res = await api.post(`/orders/${orderId}/payments`, {
        amount: Number(newPayment.amount),
        method: newPayment.method,
        reference: newPayment.reference || null,
        notes: newPayment.notes || null
      });

      if (res.data?.success) {
        setDialogOpen(false);
        setNewPayment({ amount: '', method: 'CASH', reference: '', notes: '' });
        loadPayments();
        if (onPaymentRecorded) {
          onPaymentRecorded(res.data.data);
        }
      }
    } catch (err) {
      console.error('Failed to record payment:', err);
      setError(err.response?.data?.message || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const openLayawayReceipt = () => {
    if (!paymentData) return;
    const payments = paymentData.payments || [];
    const currency = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

    const getCustomerName = (c) => {
      if (!c) return 'N/A';
      if (c.company) return c.company;
      return [c.firstName, c.lastName].filter(Boolean).join(' ');
    };

    const customerName = order ? getCustomerName(order.customer) : '';
    const addr = order?.shippingAddress || order?.customer?.address || {};
    const items = (order?.orderItems || order?.items || []).map(i => ({
      name: i.name || i.inventory?.name || 'Item',
      sku: i.sku || i.inventory?.sku || '',
      qty: i.quantity || 0,
      unit: Number(i.unitPrice || 0),
      total: Number(i.totalPrice || 0)
    }));
    const total = Number(paymentData.totalAmount || 0);
    const paid = Number(paymentData.paidAmount || 0);
    const balance = Number(paymentData.remainingBalance || 0);
    const orderNumber = order?.orderNumber || '';
    const orderDate = order?.orderDate || order?.createdAt || new Date().toISOString();

    const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Layaway Receipt ${orderNumber}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 12px; color: #111; padding: 20px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; border-bottom: 3px solid #0066cc; padding-bottom: 15px; }
    .title { font-size: 28px; font-weight: bold; color: #0066cc; margin-bottom: 15px; }
    .info-label { font-size: 10px; color: #666; font-weight: bold; margin-bottom: 5px; }
    .info-name { font-weight: bold; font-size: 14px; }
    .info-details { font-size: 11px; line-height: 1.4; }
    .company-info { text-align: right; }
    .company-logo { max-width: 150px; max-height: 60px; object-fit: contain; margin-bottom: 10px; }
    .company-name { font-weight: bold; font-size: 16px; margin-bottom: 5px; }
    .company-details { font-size: 11px; line-height: 1.4; }
    .meta-section { margin-bottom: 20px; }
    .meta-row { display: flex; margin-bottom: 4px; }
    .meta-label { font-weight: bold; color: #0066cc; width: 160px; }
    .items-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
    .items-table th { background: #0066cc; color: white; padding: 8px; text-align: left; font-size: 11px; }
    .items-table th.right { text-align: right; }
    .items-table td { padding: 8px; border-bottom: 1px solid #ddd; font-size: 11px; }
    .items-table td.right { text-align: right; }
    .totals-table { width: 260px; margin-left: auto; border-collapse: collapse; margin-bottom: 20px; }
    .totals-table td { padding: 7px 10px; border-bottom: 1px solid #ddd; }
    .totals-table .label { font-weight: bold; }
    .totals-table .value { text-align: right; font-weight: bold; }
    .totals-table .total-row { background: #f5f5f5; font-size: 14px; }
    .totals-table .balance-row td { color: #dc3545; font-size: 14px; }
    .history-title { font-size: 14px; font-weight: bold; color: #0066cc; margin-bottom: 10px; border-top: 2px solid #0066cc; padding-top: 15px; }
    .history-table { width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 20px; }
    .history-table th { background: #f5f5f5; padding: 8px; text-align: left; border-bottom: 1px solid #ddd; }
    .history-table td { padding: 6px 8px; border-bottom: 1px solid #eee; }
    .history-table .right { text-align: right; }
    .footer { margin-top: 20px; padding-top: 15px; border-top: 1px solid #ddd; }
    .footer-title { font-weight: bold; margin-bottom: 5px; }
    .footer-text { font-size: 10px; color: #666; line-height: 1.6; }
    .all-sales-final { margin-top: 10px; font-size: 15px; font-weight: bold; letter-spacing: 0.5px; color: #111; text-align: center; text-transform: uppercase; }
    .sig-section { margin-top: 30px; display: flex; justify-content: space-between; gap: 40px; }
    .sig-box { flex: 1; }
    .sig-line { border-top: 1px solid #333; margin-top: 30px; padding-top: 4px; font-size: 10px; color: #666; }
    @media print { body { padding: 0; } .no-print { display: none; } }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="title">LAYAWAY RECEIPT</div>
      <div class="info-label">CUSTOMER</div>
      <div class="info-name">${customerName.toUpperCase()}</div>
      <div class="info-details">
        ${order?.customer?.email ? `${order.customer.email}<br>` : ''}
        ${addr.street ? `${addr.street}<br>` : ''}
        ${addr.city ? `${addr.city}, ${addr.state || ''} ${addr.zipCode || ''}<br>` : ''}
        ${order?.customer?.phone || ''}
      </div>
    </div>
    <div class="company-info">
      ${order?.location?.settings?.logo ? `<img src="${order.location.settings.logo}" alt="Logo" class="company-logo">` : ''}
      <div class="company-name">${order?.location?.name || 'Company Name'}</div>
      <div class="company-details">
        ${order?.location?.street || order?.location?.address?.street ? `${order?.location?.address?.street || order?.location?.street}<br>` : ''}
        ${order?.location?.city || order?.location?.address?.city ? `${order?.location?.address?.city || order?.location?.city}, ${order?.location?.address?.state || order?.location?.state || ''} ${order?.location?.address?.zipCode || order?.location?.zipCode || ''}<br>` : ''}
        ${order?.location?.phone ? `${order.location.phone}<br>` : ''}
        ${order?.location?.email || ''}
      </div>
    </div>
  </div>

  <div class="meta-section">
    <div class="meta-row"><div class="meta-label">ORDER NO</div><div>${orderNumber}</div></div>
    <div class="meta-row"><div class="meta-label">DATE</div><div>${new Date(orderDate).toLocaleString()}</div></div>
    <div class="meta-row"><div class="meta-label">PAYMENT METHOD</div><div>LAYAWAY</div></div>
    <div class="meta-row"><div class="meta-label">PAYMENT STATUS</div><div>${(paymentData.paymentStatus || '').replace('_', ' ')}</div></div>
  </div>

  ${items.length > 0 ? `
  <table class="items-table">
    <thead>
      <tr>
        <th style="width:50%">ITEM</th>
        <th style="width:15%">SKU</th>
        <th class="right" style="width:10%">QTY</th>
        <th class="right" style="width:12%">UNIT PRICE</th>
        <th class="right" style="width:13%">TOTAL</th>
      </tr>
    </thead>
    <tbody>
      ${items.map(i => `
      <tr>
        <td>${i.name}</td>
        <td>${i.sku}</td>
        <td class="right">${i.qty}</td>
        <td class="right">${currency(i.unit)}</td>
        <td class="right">${currency(i.total)}</td>
      </tr>`).join('')}
    </tbody>
  </table>` : ''}

  <table class="totals-table">
    <tr class="total-row"><td class="label">TOTAL:</td><td class="value">${currency(total)}</td></tr>
    <tr><td class="label">TOTAL PAID:</td><td class="value" style="color:#28a745">${currency(paid)}</td></tr>
    <tr class="balance-row"><td class="label">BALANCE DUE:</td><td class="value">${currency(balance)}</td></tr>
  </table>

  ${payments.length > 0 ? `
  <div class="history-title">PAYMENT HISTORY</div>
  <table class="history-table">
    <thead>
      <tr>
        <th>Date</th>
        <th>Method</th>
        <th>Reference</th>
        <th class="right">Amount</th>
      </tr>
    </thead>
    <tbody>
      ${payments.map(p => `
      <tr>
        <td>${new Date(p.receivedAt || p.createdAt).toLocaleDateString()}</td>
        <td>${p.method || 'N/A'}</td>
        <td>${p.reference || '-'}</td>
        <td class="right">${currency(p.amount)}</td>
      </tr>`).join('')}
    </tbody>
    <tfoot>
      <tr style="font-weight:bold;background:#f9f9f9">
        <td colspan="3" style="padding:8px">Total Payments</td>
        <td style="padding:8px;text-align:right">${currency(payments.reduce((s, p) => s + Number(p.amount || 0), 0))}</td>
      </tr>
    </tfoot>
  </table>` : ''}

  <div class="sig-section">
    <div class="sig-box"><div class="sig-line">Customer Signature</div></div>
    <div class="sig-box"><div class="sig-line">Date</div></div>
    <div class="sig-box"><div class="sig-line">Staff Initials</div></div>
  </div>

  <div class="footer">
    <div class="footer-title">Layaway Terms & Conditions</div>
    <div class="footer-text">
      Items will be held until full payment is received. Layaway payments are non-refundable after 30 days.<br>
      Please bring this receipt with every payment. Balance Due: ${currency(balance)}<br>
      Thank you for your business — we appreciate your loyalty.
    </div>
    <div class="all-sales-final">All Sales Final</div>
  </div>

  <script>
    window.onload = function(){ setTimeout(function(){ window.print(); }, 200); };
  </script>
</body>
</html>`;

    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) {
      alert('Popup blocked. Please allow popups for this site to print receipts.');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => { try { w.close(); } catch (e) {} }, 5000);
  };

  const handleVoidPayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to void this payment?')) return;

    try {
      setLoading(true);
      const res = await api.delete(`/orders/${orderId}/payments/${paymentId}`);
      if (res.data?.success) {
        loadPayments();
        if (onPaymentRecorded) {
          onPaymentRecorded(res.data.data);
        }
      }
    } catch (err) {
      console.error('Failed to void payment:', err);
      setError(err.response?.data?.message || 'Failed to void payment');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PAID': return 'success';
      case 'PARTIALLY_PAID': return 'warning';
      case 'PENDING': return 'default';
      case 'FAILED': return 'error';
      default: return 'default';
    }
  };

  if (loading && !paymentData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          <PaymentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Payment History
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={openLayawayReceipt}
            disabled={!paymentData}
          >
            Print Receipt
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
            disabled={paymentData?.paymentStatus === 'PAID'}
          >
            Record Payment
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Payment Summary */}
      {paymentData && (
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">Total Amount</Typography>
              <Typography variant="h6">{formatCurrency(paymentData.totalAmount)}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">Paid</Typography>
              <Typography variant="h6" color="success.main">{formatCurrency(paymentData.paidAmount)}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">Balance Due</Typography>
              <Typography variant="h6" color={paymentData.remainingBalance > 0 ? 'error.main' : 'success.main'}>
                {formatCurrency(paymentData.remainingBalance)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">Status</Typography>
              <Chip 
                label={paymentData.paymentStatus?.replace('_', ' ')} 
                color={getStatusColor(paymentData.paymentStatus)}
                size="small"
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Payments Table */}
      {paymentData?.payments?.length > 0 ? (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Received By</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentData.payments.map((payment) => (
                <TableRow key={payment.id}>
                  <TableCell>{formatDate(payment.receivedAt)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={PAYMENT_METHODS.find(m => m.value === payment.method)?.label || payment.method} 
                      size="small" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{payment.reference || '-'}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(payment.amount)}
                  </TableCell>
                  <TableCell>
                    {payment.receivedBy ? `${payment.receivedBy.firstName} ${payment.receivedBy.lastName}` : '-'}
                  </TableCell>
                  <TableCell>{payment.notes || '-'}</TableCell>
                  <TableCell align="center">
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => handleVoidPayment(payment.id)}
                      title="Void Payment"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
          No payments recorded yet
        </Typography>
      )}

      {/* Record Payment Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Balance Due: <strong>{formatCurrency(paymentData?.remainingBalance)}</strong>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Payment Amount"
                type="number"
                value={newPayment.amount}
                onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                fullWidth
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Payment Method"
                value={newPayment.method}
                onChange={(e) => setNewPayment({ ...newPayment, method: e.target.value })}
                fullWidth
                SelectProps={{ native: true }}
              >
                {PAYMENT_METHODS.map((method) => (
                  <option key={method.value} value={method.value}>{method.label}</option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Reference (Check #, Transaction ID, etc.)"
                value={newPayment.reference}
                onChange={(e) => setNewPayment({ ...newPayment, reference: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                value={newPayment.notes}
                onChange={(e) => setNewPayment({ ...newPayment, notes: e.target.value })}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleRecordPayment}
            disabled={submitting || !newPayment.amount}
          >
            {submitting ? <CircularProgress size={20} /> : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default PaymentHistory;
